import { useState, useEffect } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import LeaderboardCard from './LeaderboardCard'
import DateRangeSelector from './DateRangeSelector'
import { getCurrentYear, createDate, getDaysInMonth, parseDateString } from '../../utils/dateUtils.js'

export default function MonthlySummaryTable({ loading: externalLoading, showLeaderboard = true, salesData = [] }) {
  const [dateRange, setDateRange] = useState('ytd')
  const [data, setData] = useState([])

  useEffect(() => {
    if (salesData && salesData.length > 0) {
      // Filter the shared data based on the selected date range
      const filteredData = filterDataByDateRange(salesData, dateRange)
      setData(filteredData)
    }
  }, [salesData, dateRange])

  const filterDataByDateRange = (salesData, range) => {
    if (range === 'all') {
      return salesData
    }

    let startDate = new Date()
    switch (range) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1)
        break
      }
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    return salesData.filter(item => parseDateString((item.isoMonth || item.month) + '-01') >= startDate)
  }

  const isLoading = externalLoading

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Monthly Sales Summary</CardTitle>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setDateRange}
              className="w-[170px]"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse">Loading summary data...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Monthly Sales Summary</CardTitle>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setDateRange}
              className="w-[170px]"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No data available for the selected period
          </div>
        </CardContent>
      </Card>
    )
  }

  // Process data to include calculated metrics (newest first)
  const now = new Date()
  const processedData = [...data].reverse().map(item => {
    // Calculate ADV (Average Daily Volume) - volume divided by days elapsed
    const monthDate = parseDateString((item.isoMonth || item.month) + '-01')
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    // For the current month, use days elapsed so far; for past months, use total days
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth()
    const daysDivisor = isCurrentMonth ? now.getDate() : daysInMonth
    const adv = daysDivisor > 0 ? (item.volume / daysDivisor) : 0
    const allAdv = daysDivisor > 0 ? ((item.allVolume || item.volume) / daysDivisor) : 0

    return {
      ...item,
      asp: (item.asp || 0).toFixed(2),
      adv: adv.toFixed(1),
      output: item.volume,
      allVolume: item.allVolume || item.volume,
      allValue: item.allValue || item.value,
      allAsp: (item.allAsp || item.asp || 0).toFixed(2),
      allAdv: allAdv.toFixed(1),
      allOutput: item.allVolume || item.volume,
      isProjected: false
    }
  })

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value)
  }

  // Format weight
  const formatWeight = (value) => {
    return `${value.toFixed(2)} kg`
  }

  return (
    <div className="space-y-6">
      <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            Monthly Sales Summary
            <Badge variant="secondary" className="ml-2">
              {processedData.length} months
            </Badge>
          </div>
          <DateRangeSelector 
            value={dateRange} 
            onValueChange={setDateRange}
            className="w-[170px]"
          />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold text-foreground">Month</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">Volume (kg)</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">Sale Value</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">ASP (£)</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">ADV (kg)</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">Output (kg)</TableHead>
                <TableHead className="text-right font-semibold text-foreground whitespace-nowrap">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {processedData.map((row, index) => (
                <TableRow 
                  key={row.month} 
                  className={
                    row.isProjected 
                      ? "bg-orange-50 dark:bg-orange-950/10 hover:bg-orange-100 dark:hover:bg-orange-950/20" 
                      : index % 2 === 0 
                        ? "hover:bg-muted/50" 
                        : "bg-muted/20 hover:bg-muted/50"
                  }
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {row.month}
                      {row.isProjected && (
                        <Badge variant="outline" size="sm">
                          Projected
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right cursor-default" title={`Incl. pending: ${formatWeight(row.allVolume)}`}>
                    {formatWeight(row.volume)}
                  </TableCell>
                  <TableCell className="text-right cursor-default" title={`Incl. pending: ${formatCurrency(row.allValue)}`}>
                    {formatCurrency(row.value)}
                  </TableCell>
                  <TableCell className="text-right cursor-default" title={`Incl. pending: £${row.allAsp}`}>
                    £{row.asp}
                  </TableCell>
                  <TableCell className="text-right cursor-default" title={`Incl. pending: ${row.allAdv} kg`}>
                    {row.adv} kg
                  </TableCell>
                  <TableCell className="text-right cursor-default" title={`Incl. pending: ${formatWeight(row.allOutput)}`}>
                    {formatWeight(row.output)}
                  </TableCell>
                  <TableCell className="text-right font-semibold cursor-default" title={`Incl. pending: ${formatCurrency(row.allValue)}`}>
                    {formatCurrency(row.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {/* Summary totals */}
        <div className="mt-6 pt-4 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center cursor-default" title={`Incl. pending: ${formatWeight(processedData.reduce((sum, row) => sum + row.allVolume, 0))}`}>
              <div className="text-2xl font-bold text-primary">
                {formatWeight(processedData.reduce((sum, row) => sum + row.volume, 0))}
              </div>
              <div className="text-sm text-muted-foreground">Total Volume</div>
            </div>
            <div className="text-center cursor-default" title={`Incl. pending: ${formatCurrency(processedData.reduce((sum, row) => sum + row.allValue, 0))}`}>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(processedData.reduce((sum, row) => sum + row.value, 0))}
              </div>
              <div className="text-sm text-muted-foreground">Total Revenue</div>
            </div>
            <div className="text-center cursor-default" title={`Incl. pending: £${(processedData.reduce((sum, row) => sum + parseFloat(row.allAsp), 0) / processedData.length).toFixed(2)}`}>
              <div className="text-2xl font-bold text-blue-600">
                £{(processedData.reduce((sum, row) => sum + parseFloat(row.asp), 0) / processedData.length).toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground">Avg ASP</div>
            </div>
            <div className="text-center cursor-default" title={`Incl. pending: ${(processedData.reduce((sum, row) => sum + parseFloat(row.allAdv), 0) / processedData.length).toFixed(1)} kg`}>
              <div className="text-2xl font-bold text-purple-600">
                {(processedData.reduce((sum, row) => sum + parseFloat(row.adv), 0) / processedData.length).toFixed(1)} kg
              </div>
              <div className="text-sm text-muted-foreground">Avg ADV</div>
            </div>
          </div>
        </div>
      </CardContent>
      </Card>

      {showLeaderboard && <LeaderboardCard data={data} loading={isLoading} />}
    </div>
  )
}